<script setup>
import { get } from 'lodash-es'
import { computed, watch } from 'vue'
import { useMainStore } from '~/composables/store'
import { legacyPathToUserCenterPath } from '~/user-center/routes'
import { getFundInfo as getUserCenterFundInfo } from '~/user-center/src/api/user'
import { formatCurrencyAmountText } from '~/user-center/src/hooks/useCurrencyRate'
import userCenterStore from '~/user-center/src/store'
import {
  COLLECTION_USER_CENTER_HOME_PATH,
  getStoredStoreInformation,
  getStoreInfoDataMapValues,
  isCollectionUserCenterStoreInfoList,
  MALL_USER_CENTER_HOME_PATH,
} from '~/utils/collectionUserCenter'
import { $t, multiplication } from '~/utils/index.js'

const props = defineProps({
  mallUserInfo: {
    type: Object,
    default: () => ({}),
  },
  logout: {
    type: Function,
    required: true,
  },
  variant: {
    type: String,
    default: 'default',
  },
  themeVars: {
    type: Object,
    default: () => ({}),
  },
})

const requestedFundInfoUserIds = new Set()
const store = useMainStore()

const userCenterLoginPath = legacyPathToUserCenterPath('/login')
const userCenterRegisterPath = legacyPathToUserCenterPath('/login?reg=1')
const rechargePath = legacyPathToUserCenterPath('/collection/recharge')
const storeInfoCandidates = computed(() => [
  ...getStoreInfoDataMapValues(store.storeInfoDataMap),
  getStoredStoreInformation(),
])
const isCollectionUserCenter = computed(() => {
  return isCollectionUserCenterStoreInfoList(storeInfoCandidates.value)
})
const userCenterHomePath = computed(() => {
  return legacyPathToUserCenterPath(
    isCollectionUserCenter.value ? COLLECTION_USER_CENTER_HOME_PATH : MALL_USER_CENTER_HOME_PATH,
  )
})

const resolvedMallUserInfo = computed(() => {
  return props.mallUserInfo?.id ? props.mallUserInfo : (store.mallUserInfo || {})
})
const isLoggedIn = computed(() => {
  return Boolean(
    resolvedMallUserInfo.value?.id
    || store.userInfo?.id
    || store.userInfo?.memberCode,
  )
})
const currentUserId = computed(() => {
  return String(
    store.userInfo?.id
    || get(userCenterStore, 'state.user.id')
    || '',
  ).trim()
})
const avatarSrc = computed(() => {
  return resolvedMallUserInfo.value?.avatarUrl || '/imgs/base-user.png'
})
const avatarSize = computed(() => (props.variant === 'simple' ? 40 : 40))
const popoverWidth = computed(() => (props.variant === 'simple' ? 320 : 320))
const fundBalance = computed(() => {
  const userCenterFundInfo = get(userCenterStore, 'state.user.fundInfo', {}) || {}
  const fundInfoUserId = String(get(userCenterFundInfo, 'userId') || '').trim()
  const isCurrentUserFundInfo = Boolean(
    get(userCenterFundInfo, 'id') || fundInfoUserId,
  ) && (!fundInfoUserId || fundInfoUserId === currentUserId.value)

  if (isCurrentUserFundInfo) {
    return get(userCenterFundInfo, 'fundBalance', 0)
  }

  return get(store.userInfo, 'fundInfo.fundBalance', get(store.userInfo, 'fundBalance', 0))
})
const defaultCurrencyCode = computed(() => {
  const userCenterCurrencyCode = String(
    get(userCenterStore, 'state.app.storeInfoData.defaultCurrencyCode') || '',
  ).trim().toUpperCase()
  if (userCenterCurrencyCode) {
    return userCenterCurrencyCode
  }

  for (const storeInfo of storeInfoCandidates.value) {
    const currencyCode = String(
      get(storeInfo, 'defaultCurrencyCode')
      || get(storeInfo, 'storePageInfo.defaultCurrencyCode')
      || '',
    ).trim().toUpperCase()

    if (currencyCode) {
      return currencyCode
    }
  }

  return String(store.getMallSiteDefaultCurrency?.() || 'CNY').trim().toUpperCase() || 'CNY'
})
const selectedCurrencyCode = computed(() => {
  return String(
    get(store.currentCurrency, 'toCurrencyCode') || defaultCurrencyCode.value,
  ).trim().toUpperCase() || defaultCurrencyCode.value
})
const currentCurrencyRate = computed(() => {
  const rate = Number(get(store.currentCurrency, 'exchangeRate'))
  return Number.isFinite(rate) && rate > 0
    ? Math.round(rate * 100) / 100
    : 0
})
const shouldShowApproxBalance = computed(() => {
  const fromCurrencyCode = String(
    get(store.currentCurrency, 'fromCurrencyCode') || defaultCurrencyCode.value,
  ).trim().toUpperCase()

  return selectedCurrencyCode.value !== defaultCurrencyCode.value
    && fromCurrencyCode === defaultCurrencyCode.value
    && currentCurrencyRate.value > 0
})
const displayedFundBalance = computed(() => fundBalance.value || 0)

function formatApproxAmount(value) {
  if (!shouldShowApproxBalance.value) {
    return ''
  }

  const convertedAmount = multiplication(value || 0, currentCurrencyRate.value)
  return formatCurrencyAmountText(convertedAmount, selectedCurrencyCode.value)
}

async function fetchFundInfoOnce(userId) {
  if (!process.client || !userId || requestedFundInfoUserIds.has(userId)) {
    return
  }

  requestedFundInfoUserIds.add(userId)
  try {
    const response = await getUserCenterFundInfo(userId)
    const fundInfo = get(response, 'data', {}) || {}
    if (currentUserId.value === userId) {
      userCenterStore.commit('user/SET_FUND_INFO', fundInfo)
    }
  }
  catch (error) {
    console.error('MallAccountEntry fetch fund info error', error)
  }
}

watch(
  () => isLoggedIn.value ? currentUserId.value : '',
  userId => void fetchFundInfoOnce(userId),
  { immediate: true },
)

const menuItems = computed(() => [
  {
    key: 'orders',
    labelKey: 'accountNav.myOrders',
    to: legacyPathToUserCenterPath('/mall/platformAllOrderListV2'),
  },
  {
    key: 'warehouse',
    labelKey: 'accountNav.myWarehouse',
    to: legacyPathToUserCenterPath('/collection/myWarehouse'),
  },
  {
    key: 'packages',
    labelKey: 'accountNav.myPackages',
    to: legacyPathToUserCenterPath('/collection/myPackage'),
  },
])
</script>

<template>
  <div class="mall-account-entry" :class="[`mall-account-entry--${variant}`]">
    <template v-if="isLoggedIn">
      <el-popover
        placement="bottom-end"
        trigger="hover"
        :show-arrow="false"
        :width="popoverWidth"
        popper-class="mall-account-entry-popper"
        :offset="12"
      >
        <template #reference>
          <NuxtLink class="mall-account-entry__avatar-link" :to="userCenterHomePath">
            <el-avatar
              :size="avatarSize"
              :src="avatarSrc"
              class="mall-account-entry__avatar"
            />
          </NuxtLink>
        </template>

        <div class="mall-account-menu">
          <div class="mall-account-menu__balance">
            <span class="mall-account-menu__balance-label">
              {{ $t("accountNav.balanceLabel") }}
            </span>
            <span class="mall-account-menu__balance-value">
              {{ shouldShowApproxBalance ?  selectedCurrencyCode : '¥'}} {{ shouldShowApproxBalance ? formatApproxAmount(displayedFundBalance) : displayedFundBalance }}
            </span>
            <NuxtLink class="mall-account-menu__recharge" :to="rechargePath">
              {{ $t("accountNav.goToRecharge") }}
            </NuxtLink>
          </div>

          <div v-if="!isCollectionUserCenter" class="mall-account-menu__items">
            <NuxtLink
              v-for="item in menuItems"
              :key="item.key"
              class="mall-account-menu__item"
              :to="item.to"
            >
              {{ $t(item.labelKey) }}
            </NuxtLink>
          </div>

          <button class="mall-account-menu__logout" type="button" @click="logout">
            {{ $t("actions.quit") }}
          </button>
        </div>
      </el-popover>
    </template>

    <template v-else>
      <div class="mall-account-entry__guest-actions">
        <NuxtLink class="mall-account-entry__ghost-button" :to="userCenterLoginPath">
          {{ $t("actions.login") }}
        </NuxtLink>
        <NuxtLink class="mall-account-entry__primary-button" :to="userCenterRegisterPath">
          {{ $t("actions.newUser") }}
        </NuxtLink>
      </div>
    </template>
  </div>
</template>

<style scoped lang="less">
.mall-account-entry {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.mall-account-entry__guest-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mall-account-entry__ghost-button,
.mall-account-entry__primary-button {
  height: 36px;

  padding: 0 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
  transition: transform 0.18s ease, opacity 0.18s ease, background-color 0.18s ease, color 0.18s ease;
}

.mall-account-entry__ghost-button {
  color: v-bind("props.themeVars.navGhostButtonTextColor || '#1a1817'");
  border: 1px solid v-bind("props.themeVars.navGhostButtonBorderColor || '#1a1817'");
  background: v-bind("props.themeVars.navGhostButtonBackground || '#fff'");
}

.mall-account-entry__primary-button {
  color: v-bind("props.themeVars.navPrimaryButtonTextColor || '#fff'");
  background: v-bind("props.themeVars.navPrimaryButtonBackground || '#ff7228'");
  border: 1px solid v-bind("props.themeVars.navPrimaryButtonBackground || '#ff7228'");
}

.mall-account-entry__ghost-button:hover,
.mall-account-entry__primary-button:hover,
.mall-account-entry__avatar-link:hover {
  opacity: 0.88;
}

.mall-account-entry__avatar-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.mall-account-entry__avatar {
  background: #fbe4d3;
  flex: 0 0 auto;
}

.mall-account-menu {
  width: 100%;
  background: v-bind("props.themeVars.navBackgroundColor || '#fff'");
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 18px 48px v-bind("props.themeVars.navShadowColor || 'rgba(26, 24, 23, 0.12)'");
}

.mall-account-menu__balance {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 20px;
  border-bottom: 1px solid v-bind("props.themeVars.navBorderColor || '#efebea'");
  font-size: 14px;
  line-height: 20px;
  color: v-bind("props.themeVars.navTextColor || '#1a1817'");
}

.mall-account-menu__balance-label {
  font-weight: 500;
}

.mall-account-menu__balance-value {
  color: v-bind("props.themeVars.navSubtleTextColor || '#978d88'");
}

.mall-account-menu__recharge {
  margin-left: auto;
  color: v-bind("props.themeVars.navActiveTextColor || '#2f74ff'");
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
}

.mall-account-menu__items {
  display: flex;
  flex-direction: column;
}

.mall-account-menu__item,
.mall-account-menu__logout {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0 20px;
  font-size: 14px;
  line-height: 20px;
  color: v-bind("props.themeVars.navTextColor || '#1a1817'");
  text-decoration: none;
  background: v-bind("props.themeVars.navBackgroundColor || '#fff'");
  border: 0;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.mall-account-menu__item:hover {
  background: v-bind("props.themeVars.navPanelHoverBackground || '#fdf2ea'");
}

.mall-account-menu__logout {
  border-top: 1px solid v-bind("props.themeVars.navBorderColor || '#efebea'");
}

.mall-account-menu__logout:hover {
  background: v-bind("props.themeVars.navPanelHoverBackground || '#faf7f6'");
}

:global(.mall-account-entry-popper) {
   padding: 0 !important;
   border-radius: 20px !important;
  border: none !important;
  // box-shadow: none !important;
  // background: transparent !important;
}
</style>
